import { Card, CardContent } from "@/components/ui/card.jsx";
import { Sparkles, Loader2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils.js";

function resolveEmptyState({
  hasEnalysisIds,
  isWrongCompanyOrAssetError,
  vitalSignsLoading,
  vitalSignsError,
  isUnauthorizedError,
}) {
  if (!hasEnalysisIds || isWrongCompanyOrAssetError)
    return { type: "not_linked" };
  if (vitalSignsLoading) return { type: "loading" };
  if (vitalSignsError)
    return isUnauthorizedError
      ? { type: "unauthorized" }
      : { type: "error", message: vitalSignsError };
  return { type: "no_data" };
}

function EmptyStateBody({ state }) {
  const { type, message } = state;
  const content = {
    not_linked: {
      Icon: Sparkles,
      iconClass: "text-muted-foreground",
      title: "Enalysis asset is not linked",
      body: (
        <>
          Enalysis is not enabled for this asset, contact your account managers
          or{" "}
          <a
            href='mailto:Sales@detechtion.com'
            className='text-brand-cyan hover:underline'>
            Sales@detechtion.com
          </a>{" "}
          to enable compressor insights.
        </>
      ),
    },
    loading: {
      Icon: Loader2,
      iconClass: "text-muted-foreground animate-spin",
      title: "Loading optimization data…",
      body: null,
    },
    unauthorized: {
      Icon: AlertCircle,
      iconClass: "text-amber-500",
      title: "Enalysis asset is linked but you do not have permissions",
      body: (
        <>
          Enalysis is enabled for this asset but you have not been provided
          permissions, contact{" "}
          <a
            href='mailto:ERC@Detechtion.com'
            className='text-brand-cyan hover:underline'>
            ERC@Detechtion.com
          </a>{" "}
          to gain access.
        </>
      ),
    },
    error: {
      Icon: AlertCircle,
      iconClass: "text-amber-500",
      title: "Could not load optimization data",
      body: message,
    },
    no_data: {
      Icon: Sparkles,
      iconClass: "text-muted-foreground",
      title: "Optimization",
      body: "No vital signs data available for this asset. When data is available, capacity, HP, diagnostics, and equipment info will appear here.",
    },
  }[type];

  if (!content) return null;
  const { Icon, iconClass, title, body } = content;
  return (
    <>
      <Icon className={cn("h-12 w-12 mb-4", iconClass)} />
      <h2 className='text-xl font-medium text-foreground mb-2'>{title}</h2>
      {body && <p className='text-muted-foreground text-center max-w-md'>{body}</p>}
    </>
  );
}

/**
 * Card shown when optimization data is missing, loading, or errored.
 */
export default function OptimizationEmptyState(props) {
  const state = resolveEmptyState(props);
  return (
    <Card className='bg-card border border-border shadow-sm'>
      <CardContent className='flex flex-col items-center justify-center py-16 px-6'>
        <EmptyStateBody state={state} />
      </CardContent>
    </Card>
  );
}
